import React from 'react';
import Button from '@atlaskit/button';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	type CustomTriggerProps,
} from '@atlaskit/dropdown-menu';
import Heading from '@atlaskit/heading';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import AdminPageWithSidebar from '@atlassian/jira-admin-pages-common/src/ui/sidebar/index.tsx';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import ErrorStateOriginal from '@atlassian/jira-common-components-error-state';
import { fg } from '@atlassian/jira-feature-gating';
import { DocumentTitle } from '@atlassian/jira-global-document-title';
import { useIntl } from '@atlassian/jira-intl';
import { ModalContextProvider } from '@atlassian/jira-modal-context-provider';
import { FireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { AsyncEditPriorityMappingModal } from '@atlassian/jira-servicedesk-incident-management-priority-mapping-modal/src/async';
import {
	PRIORITIES_ASSIGN_ALERT_PRIORITIES_DROPDOWN_ITEM_SELECTOR,
	PRIORITIES_MEATBALL_MENU_SELECTOR,
	PRIORITIES_TRANSLATE_PRIORITIES_DROPDOWN_ITEM_SELECTOR,
} from '../common/constants';
import { ERROR, LOADING, SUCCESS } from '../common/types';
import type { UsePrioritiesPageReturn } from '../controllers/priorities-page';
import { messages } from './messages';
import { PrioritiesTable } from './priorities-table';
import { PriorityModalSwitcher } from './priority-modals';
import { PrioritySidebarSwitcher } from './priority-sidebars';

export type PrioritiesPageViewProps = UsePrioritiesPageReturn;

const DropdownItemWithAnalytics = ComponentWithAnalytics('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);

export const PrioritiesPageView = (props: PrioritiesPageViewProps) => {
	const { formatMessage } = useIntl();

	const {
		data,
		state,
		currentPageNumber,
		focusedPriorityId,
		cloudId,
		isAssignPrioritiesModalOpen,
		currentModal,
		currentSidebar,
		asyncFormErrors,
		setAsyncFormErrors,
		onAssignPrioritiesModalToggle,
		closePriorityModal,
		closePrioritySidebar,
		onFetch,
		onReorder,
		setCurrentModal,
		setCurrentSidebar,
		onSetPriorityPosition,
		onCreateConfirm,
		onEditConfirm,
		onDeleteConfirm,
	} = props;

	return (
		<AdminPageWithSidebar>
			<Stack space="space.200" xcss={StackStyles}>
				<DocumentTitle title={formatMessage(messages.header)} />
				<Box>
					<Inline spread="space-between">
						<Heading as="h1" size="large">
							{formatMessage(messages.header)}
						</Heading>
						<Inline space="space.100">
							<Button
								onClick={() =>
									setCurrentSidebar({
										sidebarName: 'CREATE',
									})
								}
							>
								{formatMessage(messages.addPriorityButton)}
							</Button>
							<DropdownMenu<HTMLButtonElement>
								placement="bottom-end"
								testId={PRIORITIES_MEATBALL_MENU_SELECTOR}
								trigger={({ triggerRef, ...restProps }: CustomTriggerProps) => (
									<Button
										{...restProps}
										iconBefore={<MoreIcon label={formatMessage(messages.moreButtonIconLabel)} />}
										ref={triggerRef}
									/>
								)}
								shouldRenderToParent
							>
								<DropdownItemGroup>
									<DropdownItemWithAnalytics
										testId={PRIORITIES_ASSIGN_ALERT_PRIORITIES_DROPDOWN_ITEM_SELECTOR}
										onClick={() => onAssignPrioritiesModalToggle()}
										// @ts-expect-error - Type '{ children: string; testId: string; onClick: () => void; name: string; }' is not assignable to type 'IntrinsicAttributes & Omit<{ children: ReactNode; component?: ComponentType<PropsWithChildren<CustomItemComponentProps>> | undefined; ... 14 more ...; key?: Key | ... 1 more ... | undefined; }, keyof WithAnalyticsEventsProps>'.
										name="assignAlertPriorities"
									>
										{formatMessage(messages.assignAlertPriorities)}
									</DropdownItemWithAnalytics>
									<DropdownItemWithAnalytics
										testId={PRIORITIES_TRANSLATE_PRIORITIES_DROPDOWN_ITEM_SELECTOR}
										href="/secure/admin/ViewTranslations!default.jspa?issueConstantType=priority"
										// @ts-expect-error - Type '{ children: string; testId: string; href: string; name: string; }' is not assignable to type 'IntrinsicAttributes & Omit<{ children: ReactNode; component?: ComponentType<PropsWithChildren<CustomItemComponentProps>> | undefined; ... 14 more ...; key?: Key | ... 1 more ... | undefined; }, keyof WithAnalyticsEventsProps>'.
										name="translatePriorities"
									>
										{formatMessage(messages.translatePriorities)}
									</DropdownItemWithAnalytics>
								</DropdownItemGroup>
							</DropdownMenu>
						</Inline>
					</Inline>
					<p>{formatMessage(messages.description)}</p>
				</Box>
				<Box>
					{state === ERROR ? (
						<>
							<ErrorStateOriginal
								header={formatMessage(messages.errorStateHeader)}
								description={formatMessage(messages.errorStateDescription)}
							/>
							<FireOperationalAnalytics action="failed" actionSubject="renderAdminPrioritiesPage" />
						</>
					) : (
						<>
							{fg('jsw_priority_modals_admin_page') ? (
								<ModalContextProvider>
									<PrioritiesTable
										page={data}
										currentPageNumber={currentPageNumber}
										focusedPriorityId={focusedPriorityId}
										isLoading={state === LOADING}
										setCurrentModal={setCurrentModal}
										setCurrentSidebar={setCurrentSidebar}
										setAsyncFormErrors={setAsyncFormErrors}
										onFetch={onFetch}
										onReorder={onReorder}
										onClosePriorityModal={closePriorityModal}
										onDeleteConfirm={onDeleteConfirm}
										onSetPriorityPosition={onSetPriorityPosition}
									/>
								</ModalContextProvider>
							) : (
								<PrioritiesTable
									page={data}
									currentPageNumber={currentPageNumber}
									focusedPriorityId={focusedPriorityId}
									isLoading={state === LOADING}
									setCurrentModal={setCurrentModal}
									setCurrentSidebar={setCurrentSidebar}
									setAsyncFormErrors={setAsyncFormErrors}
									onFetch={onFetch}
									onReorder={onReorder}
								/>
							)}
							{state === SUCCESS ? (
								<FireOperationalAnalytics
									action="succeeded"
									actionSubject="renderAdminPrioritiesPage"
								/>
							) : null}
						</>
					)}
				</Box>
				{isAssignPrioritiesModalOpen && (
					<AsyncEditPriorityMappingModal
						cloudId={cloudId}
						onClose={() => onAssignPrioritiesModalToggle()}
					/>
				)}

				{!fg('jsw_priority_modals_admin_page') && (
					<PriorityModalSwitcher
						onClose={closePriorityModal}
						currentModal={currentModal}
						totalItems={data.totalItems}
						asyncFormErrors={asyncFormErrors}
						onDelete={onDeleteConfirm}
						onSetPosition={onSetPriorityPosition}
					/>
				)}

				<PrioritySidebarSwitcher
					onClose={closePrioritySidebar}
					currentSidebar={currentSidebar}
					asyncFormErrors={asyncFormErrors}
					onEdit={onEditConfirm}
					onCreate={onCreateConfirm}
				/>
			</Stack>
		</AdminPageWithSidebar>
	);
};

const StackStyles = xcss({
	margin: 'space.400',
	width: '100%',
});
